import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Loader from "../../common/loader";
import Breadcrums from "../../common/breadcrumbs";
import { Details } from "../../services/product.services";
import { globalLoader } from "../../../../utils/commonfunction";

const ProductView = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const [product, setProduct] = useState(null);

  const breadcrumbs = [
    { title: t("link_dashboard"), url: "/admin/dashboard" },
    { title: "Products", url: "/admin/product/list/1" },
    { title: t("View"), url: "" },
  ];

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return `${process.env.REACT_APP_API_URL || ""}/${imagePath}`;
  };

  useEffect(() => {
    if (id) {
      globalLoader(true);
      Details(id)
        .then((response) => {
          if (response.success) {
            setProduct(response.data);
          }
          globalLoader(false);
        })
        .catch((error) => {
          console.log("error fetching details", error);
          globalLoader(false);
        });
    }
  }, [id]);

  if (!product) {
    return (
      <>
        <Breadcrums data={breadcrumbs} />
        <div className="card custom-card">
          <div className="card-body">
            <Loader />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="main-content-label mb-1">{product.name}</h5>
                <div className="d-flex flex-wrap gap-2 align-items-center mt-2">
                  <span className="badge bg-primary fs-6">
                    Category: {product.category?.title || "N/A"}
                  </span>
                  {product.gst_percent !== undefined && product.gst_percent !== null && (
                    <span className="badge bg-info fs-6">
                      GST: {product.gst_percent}%
                    </span>
                  )}
                </div>
              </div>
              <button
                className="btn ripple btn-dark"
                type="button"
                onClick={() => navigate(-1)}
              >
                <i className="fa fa-arrow-left me-1"></i> {t("btn_back")}
              </button>
            </div>

            {/* Sub-Products List Section */}
            {product.sub_products && product.sub_products.length > 0 ? (
              <div className="row">
                <div className="col-lg-12">
                  <h6 className="main-content-label mb-3">Sub-Products & Variants</h6>
                  {product.sub_products.map((sub, index) => {
                    const mrpInr = sub.mrp_inr ?? sub.mrp ?? 0;
                    const mrpUsd = sub.mrp_usd ?? 0;
                    const rawKeywords = sub.keywords;
                    const keywordsList = rawKeywords
                      ? (typeof rawKeywords === "string" ? rawKeywords.split(",") : Array.isArray(rawKeywords) ? rawKeywords : [String(rawKeywords)])
                      : [];

                    return (
                      <div
                        key={index}
                        className="card border mb-4 shadow-sm rounded overflow-hidden"
                      >
                        <div className="card-header bg-light d-flex justify-content-between align-items-center py-2 px-3">
                          <h6 className="mb-0 text-primary fw-bold">
                            <i className="fa fa-cubes me-2"></i>
                            {sub.name || `Sub-Product ${index + 1}`}
                          </h6>
                          <div>
                            {Number(sub.status) === 1 || sub.status === true ? (
                              <span className="badge bg-success me-2">{t("btn_active")}</span>
                            ) : (
                              <span className="badge bg-secondary me-2">{t("btn_inactive")}</span>
                            )}
                            {sub.is_returnable ? (
                              <span className="badge bg-info">Returnable</span>
                            ) : (
                              <span className="badge bg-light text-dark border">Non-Returnable</span>
                            )}
                          </div>
                        </div>

                        <div className="card-body">
                          {/* Attributes Tags for this Sub-Product */}
                          {sub.attributes && sub.attributes.length > 0 && (
                            <div className="mb-3">
                              <small className="text-muted d-block fw-bold mb-1">Variant Attributes:</small>
                              <div className="d-flex flex-wrap gap-2">
                                {sub.attributes.map((attr, aIdx) => (
                                  <span key={aIdx} className="badge bg-light text-dark border p-2 fs-6">
                                    <strong>{attr.attribute?.title || "Attribute"}:</strong>{" "}
                                    {attr.attribute_value?.value || "Value"}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Pricing & Stock Details Grid */}
                          <div className="row mb-3 p-3 bg-light rounded mx-0">
                            <div className="col-md-2 col-6 mb-2">
                              <small className="text-muted d-block fw-bold">MRP (₹)</small>
                              <span className="fw-bold text-muted text-decoration-line-through fs-6">
                                ₹ {mrpInr}
                              </span>
                            </div>
                            <div className="col-md-2 col-6 mb-2">
                              <small className="text-muted d-block fw-bold">Selling Price (₹)</small>
                              <span className="fw-bold text-dark fs-6">₹ {sub.price || 0}</span>
                            </div>
                            <div className="col-md-2 col-6 mb-2">
                              <small className="text-muted d-block fw-bold">MRP (USD $)</small>
                              <span className="fw-bold text-muted text-decoration-line-through fs-6">
                                $ {mrpUsd}
                              </span>
                            </div>
                            <div className="col-md-2 col-6 mb-2">
                              <small className="text-muted d-block fw-bold">Selling Price (USD $)</small>
                              <span className="fw-bold text-dark fs-6">$ {sub.price_usd || 0}</span>
                            </div>
                            <div className="col-md-2 col-6 mb-2">
                              <small className="text-muted d-block fw-bold">Discount</small>
                              <span className="fw-bold text-success fs-6">{sub.discount_percentage || 0}%</span>
                            </div>
                            <div className="col-md-2 col-6 mb-2">
                              <small className="text-muted d-block fw-bold">Total Stock</small>
                              <span className="fw-bold text-dark fs-6">{sub.total_stock || 0}</span>
                            </div>
                            {sub.sold_stock !== undefined && sub.sold_stock !== null && (
                              <div className="col-md-2 col-6 mb-2 mt-2">
                                <small className="text-muted d-block fw-bold">Sold Stock</small>
                                <span className="fw-bold text-secondary fs-6">{sub.sold_stock}</span>
                              </div>
                            )}
                            {sub.available_stock !== undefined && sub.available_stock !== null && (
                              <div className="col-md-2 col-6 mb-2 mt-2">
                                <small className="text-muted d-block fw-bold">Available Stock</small>
                                <span className="fw-bold text-primary fs-6">{sub.available_stock}</span>
                              </div>
                            )}
                          </div>

                          {/* Dimensions, Weight, Certificate */}
                          <div className="row mb-3">
                            <div className="col-md-3 col-6 mb-2">
                              <small className="text-muted d-block fw-bold">Weight</small>
                              <span>{sub.weight ? `${sub.weight} kg` : "N/A"}</span>
                            </div>
                            <div className="col-md-4 col-6 mb-2">
                              <small className="text-muted d-block fw-bold">Dimensions (L x W x H)</small>
                              <span>
                                {sub.length || sub.width || sub.height
                                  ? `${sub.length || 0} x ${sub.width || 0} x ${sub.height || 0} cm`
                                  : "N/A"}
                              </span>
                            </div>
                            <div className="col-md-5 col-12 mb-2">
                              <small className="text-muted d-block fw-bold">Certificate Document</small>
                              {sub.certificate ? (
                                <a
                                  href={getImageUrl(sub.certificate)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-primary mt-1"
                                >
                                  <i className="fa fa-file-text-o me-1"></i> View / Download Certificate
                                </a>
                              ) : (
                                <span className="text-muted">N/A</span>
                              )}
                            </div>
                          </div>

                          {/* Keywords / Tags */}
                          {keywordsList && keywordsList.length > 0 && keywordsList.some(k => k && String(k).trim()) && (
                            <div className="mb-3">
                              <small className="text-muted d-block fw-bold mb-1">Keywords / Tags:</small>
                              <div className="d-flex flex-wrap gap-1">
                                {keywordsList.map((kw, kIdx) => (
                                  <span key={kIdx} className="badge bg-secondary me-1">
                                    {typeof kw === "string" ? kw.trim() : kw}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Short Description */}
                          {sub.description && (
                            <div className="mb-3">
                              <small className="text-muted d-block fw-bold mb-1">Short Description:</small>
                              <p className="mb-0 text-secondary bg-light p-2 rounded">{sub.description}</p>
                            </div>
                          )}

                          {/* Detailed Description */}
                          {sub.about_description && (
                            <div className="mb-3">
                              <small className="text-muted d-block fw-bold mb-1">About Product (Detailed):</small>
                              <div
                                className="p-3 border rounded bg-white"
                                dangerouslySetInnerHTML={{ __html: sub.about_description }}
                              />
                            </div>
                          )}

                          {/* Sub-Product Images */}
                          <div>
                            <small className="text-muted d-block fw-bold mb-2">Sub-Product Gallery Images:</small>
                            {sub.images && sub.images.length > 0 ? (
                              <div className="d-flex flex-wrap gap-2">
                                {sub.images.map((img, imgIdx) => (
                                  <div
                                    key={imgIdx}
                                    className="border rounded overflow-hidden shadow-sm"
                                    style={{ width: "100px", height: "100px", cursor: "pointer" }}
                                    onClick={() => window.open(getImageUrl(img.image), "_blank")}
                                  >
                                    <img
                                      src={getImageUrl(img.image)}
                                      alt={`Sub-Product Image ${imgIdx + 1}`}
                                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                    />
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-muted small mb-0">No specific images uploaded for this sub-product.</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="alert alert-info">No sub-products found for this product.</div>
            )}

            {/* Fallback Main Product Gallery */}
            {product.images && product.images.length > 0 && (
              <div className="col-lg-12 mt-4">
                <h6 className="main-content-label mb-3">Main Product Gallery</h6>
                <div className="row">
                  {product.images.map((img, index) => (
                    <div key={index} className="col-lg-3 col-md-4 col-sm-6 mb-3">
                      <div className="gallery-item border rounded overflow-hidden shadow-sm">
                        <img
                          src={getImageUrl(img.image)}
                          alt={`Product ${index + 1}`}
                          style={{
                            width: "100%",
                            height: "200px",
                            objectFit: "cover",
                            cursor: "pointer",
                          }}
                          onClick={() => window.open(getImageUrl(img.image), "_blank")}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductView;
